import React, { useEffect, useCallback,useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { Tag, Space, notification, Modal, Button } from 'antd';
import { EyeOutlined, EditOutlined,DeleteOutlined } from '@ant-design/icons';
import { useUserLocale } from '../../hooks/useLocale';
import { useCaseList } from '../../hooks/cases/useCaseList';
import { APP_TERMS } from '../../app-terms';
import { AvailableAppLocales } from '../../availableAppLocales';
import ListPage from '../../components/core/ListPage';
import useListFilters from '../../hooks/useListFilters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../axios/apiService';
import {
  CASE_STATUS_CREATED,
  CASE_STATUS_SUBMITTED,
} from './CaseConstants';
import PaypalPaymentButton from '../../components/case/PaypalPaymentButton';
import { PaymentFor } from '../../appContants';
const deleteCaseConfig = {
  title: 'Cancel Case',
  content: 'Are you sure you want to cancel this case?',
  okText: 'Yes',
  cancelText: 'No',
};
const CaseList = ({ title, caseStatus, breadCrumbItems, onItemDelete }) => {
  const [modal, modalContextHolder] = Modal.useModal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userLocale } = useUserLocale();
  const { data, getCaseList } = useCaseList();
  const {
    loading,
    search,
    tableParams,
    setLoading,
    setSearch,
    setTableParams,
  } = useListFilters();
  const [api, contextHolder] = notification.useNotification();
  const [hasRushstatusFilter, setHasRushStatusFilter] = useState(caseStatus === CASE_STATUS_SUBMITTED && searchParams.get('rushOnly') === '1');

  // Handle "Has Rush" checkbox filter (true/false)
  const handleStatusChange = (checked) => {
    setHasRushStatusFilter(!!checked);
  };
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCaseList({
        hasRush:hasRushstatusFilter? true:undefined,
        search,
        page: tableParams.pagination?.current,
        limit: tableParams.pagination?.pageSize,
        sortOrder: tableParams?.sortOrder,
        sortBy: tableParams?.sortField,
        caseStatus,
      });
      setLoading(false);
      // Update table params
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: response.totalItems,
        },
      });
    } catch (error) {
      setLoading(false);
    }
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams?.sortOrder,
    tableParams?.sortField,
    search,
    caseStatus,
    hasRushstatusFilter
  ]);
  const handleTableChange = (pagination, _, sorter) => {
    setTableParams({
      pagination,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
  };

  const handleAdd = () => navigate('/user/case/add');
  const handleEdit = (record) =>
    navigate(`/user/case/edit?id=${record?.caseId}`);
  const handleView = (record) =>
    navigate(`/user/case/view?id=${record?.caseId}`);
  const handleDelete = async (record) => {
    try {
      const confirmed = await modal.confirm(deleteCaseConfig);
      if (!confirmed) return;
      setLoading(true);
      const response = await apiService.deleteCase(record?.caseId);
      api.success({
        message: 'Success',
        description: response?.message,
        showProgress: false,
      });
      reload();
      onItemDelete?.();
    } catch (error) {
      api.error({
        message: 'Error',
        description: error?.message,
        showProgress: false,
      });
    } finally {
      setLoading(false);
    }
  };
  const handleMarkAsSubmitted = async (record) => {
    try {
      setLoading(record?.caseId);
      const response = await apiService.markCaseAsSubmitted(record?.caseId);
      api.success({
        message: 'Success',
        description: response?.message,
        showProgress: false,
      });
      reload();
    } catch (error) {
      api.error({
        message: 'Error',
        description: error?.message,
        showProgress: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams?.sortOrder,
    tableParams?.sortField,
    search,
    caseStatus,
    hasRushstatusFilter
  ]);
  const columns = [
    {
      title: 'Case ID',
      dataIndex: 'caseId',
    },
    {
      title: 'Patient Name',
      dataIndex: 'patientName',
    },
    {
      title: 'Referring Doctor',
      dataIndex: 'doctorName',
      sorter: true,
    },
    {
      title: 'Services',
      dataIndex: 'caseServices',
      render: (services) => (
        <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0 }}>
          {services?.map((service) => (
            <li key={service} style={{ textWrap: 'wrap' }}>
              {service}
            </li>
          ))}
        </ul>
      ),
    },
    {
      title: 'Rush Status',
      key: 'hasRush',
      dataIndex: 'hasRush',
      render: (value, record, index) => {
        // const statusColor = getCaseStatusColor(record?.caseStatus);
        return (
          record.hasRush ?
            <Tag color={"red"} key={index}>
              RUSH
            </Tag> : "-"
        );
      },
      sorter: true,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      render: (text) =>
        format(new Date(text), APP_TERMS.date_1_format, {
          locale: AvailableAppLocales?.[userLocale],
        }),
      sorter: true,
    },
    {
      title: '',
      dataIndex: '',
      render: (_, record) => {
        return (
          <Space size="middle">
            {CASE_STATUS_CREATED === record?.caseStatus ? (
              <PaypalPaymentButton
                paymentFor={PaymentFor.CASE}
                loading={loading === record?.caseId}
                recordId={record?.caseId}
                type="primary"
                htmlType="button"
                onMarkAsSubmitted={() => handleMarkAsSubmitted(record)}
                disabled={!!loading}
                title={'Submit'}
              />
            ) : null}
            <Button key="ok" type="primary" icon={<EyeOutlined />} onClick={() => handleView(record)} />
            {CASE_STATUS_CREATED === caseStatus ? (
              <Button
                disabled={CASE_STATUS_SUBMITTED === caseStatus}
                key="ok"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            ) : null}
            {CASE_STATUS_CREATED === caseStatus ? (
              <Button
                disabled={CASE_STATUS_CREATED !== caseStatus}
                key="ok"
                danger
                type="primary"
                onClick={() => handleDelete(record)}
                icon={<DeleteOutlined />}
              />
              
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      {contextHolder}
      <ListPage
        title={title}
        data={data?.items || []}
        columns={columns}
        loading={loading}
        pagination={tableParams.pagination}
        rowKey={(record) => record?.caseId}
        onAdd={handleAdd}
        onRefresh={reload}
        onSearch={setSearch}
        onTableChange={handleTableChange}
        breadCrumbItems={breadCrumbItems}
        showHasStatusFilter
        hasRushFilterChecked={hasRushstatusFilter}
        onhasRushStatusChange={handleStatusChange}
      />
      {modalContextHolder}
    </>
  );
};

CaseList.propTypes = {
  title: PropTypes.string.isRequired,
  caseStatus: PropTypes.string.isRequired,
  breadCrumbItems: PropTypes.array,
  onItemDelete: PropTypes.func,
};

export default CaseList;
